import { HSpacer, Text, TextLink } from "@/components/DesignSystem";
import { EditSubwalletModal } from '@/components/shared/Subwallet/EditSubwalletModal';
import { QueryKeys } from "@/constants/QueryKeys";
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { SubwalletApi } from '@/utilities/api/SubwalletApi';
import { ApiPromotion, ApiRetailer } from "@api/interfaces";
import Add from "@mui/icons-material/Add";
import Edit from "@mui/icons-material/Edit";
import { Stack, useTheme } from "@mui/material";
import React, { useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from 'react-i18next';

type SaveSubwalletButtonBaseProps = {
  onSaveSubwallet: (subwalletId: string) => void,
};

type SaveSubwalletButtonPromotionProps = SaveSubwalletButtonBaseProps & {
  promotion: ApiPromotion,
  retailer?: never,
};

type SaveSubwalletButtonRetailerProps = SaveSubwalletButtonBaseProps & {
  promotion?: never,
  retailer: ApiRetailer,
};

type SaveSubwalletButtonProps =
  | SaveSubwalletButtonPromotionProps
  | SaveSubwalletButtonRetailerProps;

export const SaveSubwalletButton = ({
  onSaveSubwallet,
  promotion,
  retailer,
}: SaveSubwalletButtonProps) => {
  const { t } = useTranslation();
  const [showEditSubwalletModal, setShowEditSubwalletModal] = useState(false);
  const theme = useTheme();
  const id = promotion?.id ?? retailer?.id;
  const subwalletId = promotion?.subwalletId || retailer?.programSubwalletId;
  const { isInternal } = useAuthentication();

  const { data: hasPointsHistory } = useQuery(
    [QueryKeys.HAS_POINTS_HISTORY, id, subwalletId],
    () => SubwalletApi.hasPointsHistory(
      subwalletId!,
      { promotionId: promotion?.id },
    ),
    { enabled: !!subwalletId },
  );

  const subwallet = promotion?.subwallet ?? retailer?.programSubwallet;
  return (
    <>
      {!subwallet && isInternal && (
        <TextLink
          fontSize={14}
          onClick={() => setShowEditSubwalletModal(true)}
          testID={`${id}-subwallet-text-link`}
          width="max-content"
        >
          <Add />
          {t($ => $.shared.subwallet.add)}
        </TextLink>
      )}
      {!!subwallet && !hasPointsHistory && (
        <Stack
          alignItems="center"
          direction="row"
          onClick={() => isInternal && setShowEditSubwalletModal(true)}
          sx={{ cursor: isInternal ? 'pointer' : undefined }}
        >
          <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
            {t($ => $.shared.subwallet.label)}
          </Text>
          <HSpacer size="3" />
          <Text category="body-medium">
            {subwallet.name}
          </Text>
          {isInternal && (
            <>
              <HSpacer size="3" />
              <Edit color="primary" sx={{ width: '20px' }} />
            </>
          )}
        </Stack>
      )}
      {!!subwallet && hasPointsHistory && (
        <Stack alignItems="center" direction="row">
          <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
            {t($ => $.shared.subwallet.label)}
          </Text>
          <HSpacer size="3" />
          <Text category="body-medium">
            {subwallet.name}
          </Text>
        </Stack>
      )}
      {showEditSubwalletModal && (
        <EditSubwalletModal
          {...(promotion ? { promotion } : { retailer: retailer })}
          currentSubwalletId={promotion?.subwalletId ?? retailer?.programSubwalletId ?? null}
          onClose={() => setShowEditSubwalletModal(false)}
          onSave={async (subwalletId) => {
            setShowEditSubwalletModal(false);
            onSaveSubwallet(subwalletId);
          }}
        />
      )}
    </>
  );
};
